import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "../lib/utils";
import { MediaSlotView } from "../render/context";
import { Card, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Section } from "./_section";
/**
 * FeatureGridBlock — data-driven responsive grid of feature cards.
 *
 * Net-new, serializable contract (see @diality/contracts FeatureGridData).
 * Rendered identically by the CMS canvas and the public site; only image
 * resolution differs, via BlockRenderProvider.
 */
const CONTAINER = "mx-auto max-w-7xl px-4 @min-[640px]:px-6 @min-[1024px]:px-8";
export function FeatureGridBlock({ data }) {
    const { heading, body, items } = data;
    const columns = data.columns ?? 3;
    const gridCols = columns === 2
        ? "@min-[640px]:grid-cols-2"
        : columns === 4
            ? "@min-[640px]:grid-cols-2 @min-[1024px]:grid-cols-4"
            : "@min-[640px]:grid-cols-2 @min-[1024px]:grid-cols-3";
    // Trailing-orphan fix: at the sm breakpoint the grid is 2-up, so an odd last
    // card would otherwise sit alone half-width. Span it across both columns and
    // centre it (max-w-md) so it reads as intentional; reset at lg where the wider
    // grids lay out on their own. These are :nth-child rules on the grid's own DOM
    // siblings (the cards), identical on canvas + public — unlike cross-block band
    // tone, which is index-driven.
    const orphanFix = "[&>*:last-child:nth-child(odd)]:@min-[640px]:col-span-2 [&>*:last-child:nth-child(odd)]:@min-[640px]:mx-auto [&>*:last-child:nth-child(odd)]:@min-[640px]:w-full [&>*:last-child:nth-child(odd)]:@min-[640px]:max-w-md [&>*:last-child:nth-child(odd)]:@min-[1024px]:col-span-1 [&>*:last-child:nth-child(odd)]:@min-[1024px]:mx-0 [&>*:last-child:nth-child(odd)]:@min-[1024px]:max-w-none";
    return (_jsx(Section, { children: _jsxs("div", { className: CONTAINER, children: [(heading || body) && (_jsxs("div", { className: "mx-auto mb-12 flex max-w-3xl flex-col items-center gap-4 text-center", children: [heading && (_jsx("h2", { className: "text-3xl font-bold tracking-tight", children: heading })), body && _jsx("p", { className: "text-base text-muted-foreground @min-[640px]:text-lg", children: body })] })), _jsx("div", { className: cn("grid gap-6", gridCols, orphanFix), children: items.map((item, i) => (_jsx(Card, { className: "flex h-full flex-col transition-shadow hover:shadow-card-hover", children: _jsxs(CardHeader, { children: [_jsx("div", { className: "mb-4 inline-flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary", children: _jsx(MediaSlotView, { image: item.icon, ratio: "1 / 1", label: "Icon", className: "size-11 rounded-full", emptyRender: _jsx("span", { "aria-hidden": "true", className: "size-2 rounded-full bg-primary" }) }) }), _jsx(CardTitle, { className: "text-lg", children: item.title }), item.description && _jsx(CardDescription, { children: item.description })] }) }, i))) })] }) }));
}
export function featureGridFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    const columns = ([2, 3, 4].includes(Number(s.columns)) ? Number(s.columns) : 3);
    const items = (block.repeats?.items ?? []).map((it) => ({
        title: it.scalars?.title || "",
        description: it.scalars?.description || undefined,
        icon: resolve(it.media?.icon),
    }));
    return {
        heading: s.heading || undefined,
        body: s.body || undefined,
        columns,
        items,
    };
}
export const featureGridBlockEntry = {
    Component: FeatureGridBlock,
    fromBlock: featureGridFromBlock,
};
//# sourceMappingURL=feature-grid.js.map